/**
 * Elternbrief übernehmen: schreibt die angehakten Vorschläge über die
 * **vorhandenen** Schreibfunktionen — `saveEvent` für Termine (im Haushalt
 * geteilt, „für wen“ = das Kind), `addTask` für Aufgaben (in einer Liste,
 * zuständig = das Kind, wenn die Liste geteilt ist). Damit gelten dieselben
 * Regeln wie beim Anlegen von Hand: lokal zuerst, dann Outbox, Abgleich und
 * Verschlüsselung wie für jeden Termin. Das Foto wird nie gespeichert.
 */

import type { KraftwerkDatabase, LocalTaskList } from '../db/local.js';
import { db } from '../db/local.js';
import { saveEvent } from '../db/calendar.js';
import { addTask, createTaskList } from '../db/tasks.js';

/** Ein Vorschlag, wie ihn der Nutzer zuletzt gesehen und bearbeitet hat. */
export interface LetterRow {
  key: string;
  selected: boolean;
  kind: 'event' | 'task';
  title: string;
  /** `YYYY-MM-DD` oder leer. */
  date: string;
  /** `HH:MM` oder leer (ganztägig bzw. ohne Uhrzeit). */
  time: string;
  durationMin: number | null;
  endDate: string | null;
  /** Für welches Mitglied (meist das Kind); leer = für alle. */
  forUserId: string;
  /** Aufgabenliste oder `NEW_LIST`. */
  listId: string;
}

export const NEW_LIST = 'new';

/** Darf diese Zeile so übernommen werden? Termine brauchen Titel und Datum. */
export function rowIsComplete(row: LetterRow): boolean {
  if (row.title.trim() === '') return false;
  if (row.kind === 'event') return row.date !== '';
  return row.listId !== '';
}

/**
 * Vorgabe für die Liste: eine geteilte Liste „Schule“/„Kita“, sonst die erste
 * geteilte, ohne Haushalt die erste eigene — sonst eine neue.
 */
export function defaultListId(lists: LocalTaskList[], householdId: string | null): string {
  const usable = lists.filter(
    (list) => list.kind === 'tasks' && (householdId ? list.householdId === householdId : true),
  );
  const school = usable.find((list) => /schule|kita|school|kindergarten/i.test(list.name));
  return (school ?? usable[0])?.id ?? NEW_LIST;
}

export async function applyLetter(
  rows: LetterRow[],
  context: { householdId: string | null; newListName: string },
  database: KraftwerkDatabase = db,
): Promise<{ events: number; tasks: number }> {
  const chosen = rows.filter((row) => row.selected && rowIsComplete(row));
  let createdList: LocalTaskList | null = null;
  let events = 0;
  let tasks = 0;
  for (const row of chosen) {
    if (row.kind === 'event') {
      const shared = context.householdId !== null;
      await saveEvent(
        {
          title: row.title,
          date: row.date,
          time: row.time || null,
          durationMin: row.time ? row.durationMin : null,
          endDate: row.time ? null : row.endDate,
          location: null,
          note: null,
          activity: null,
          activityMin: null,
          cancelled: false,
          shared,
          forUserIds: shared && row.forUserId ? [row.forUserId] : [],
        },
        null,
        context.householdId,
        database,
      );
      events += 1;
      continue;
    }
    let listId = row.listId;
    if (listId === NEW_LIST) {
      createdList ??= await createTaskList(
        { name: context.newListName, kind: 'tasks', householdId: context.householdId },
        database,
      );
      listId = createdList.id;
    }
    const list = await database.task_lists.get(listId);
    const written = await addTask(
      listId,
      {
        title: row.title,
        note: null,
        dueDate: row.date || null,
        dueTime: row.date && row.time ? row.time : null,
        repeat: null,
        assigneeId: list?.householdId && row.forUserId ? row.forUserId : null,
        parentId: null,
      },
      database,
    );
    if (written) tasks += 1;
  }
  return { events, tasks };
}
